import { Component } from '@theme/component';
import {
  debounce,
  onDocumentLoaded,
  setHeaderMenuStyle,
} from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #activeItem = null;
  #activeSubmenu = null;
  #isOverflow = false;
  #closeTimer = null;
  #measureFrame = null;
  #resizeObserver = null;
  #mutationObserver = null;
  #connectionVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    const version = ++this.#connectionVersion;

    window.addEventListener('resize', this.#onWindowResize);
    this.addEventListener('pointerenter', this.#cancelClose);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('load', this.#onContentLoad, true);

    onDocumentLoaded(() => {
      if (
        !this.isConnected ||
        version !== this.#connectionVersion
      ) {
        return;
      }

      this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.removeAttribute('loading');
      });

      this.#scheduleMeasure();
    });

    document.fonts?.ready.then(() => {
      if (
        this.isConnected &&
        version === this.#connectionVersion
      ) {
        this.#scheduleMeasure();
      }
    });
  }

  disconnectedCallback() {
    ++this.#connectionVersion;

    this.#closeImmediately();

    window.removeEventListener('resize', this.#onWindowResize);
    this.removeEventListener('pointerenter', this.#cancelClose);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('load', this.#onContentLoad, true);

    super.disconnectedCallback();
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector(
      '[part="overflow"]'
    ) ?? null;
  }

  get overflowList() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector(
      '[part="overflow-list"]'
    ) ?? null;
  }

  #onWindowResize = debounce(() => {
    if (!this.isConnected) return;

    setHeaderMenuStyle();
    this.#scheduleMeasure();
  }, 100);

  #onContentLoad = () => {
    this.#scheduleMeasure();
  };

  #cancelClose = () => {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#activeItem) return;

    const item = this.#activeItem;

    event.preventDefault();
    event.stopPropagation();

    item.focus();
    this.#closeImmediately();
  };

  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = this.#findMenuItem(event.target);
    const header = this.headerComponent;

    if (!item || !header) return;

    this.#cancelClose();

    if (item === this.#activeItem) {
      this.#scheduleMeasure();
      return;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.#stopObserving();

    if (this.#activeItem) {
      this.#activeItem.ariaExpanded = 'false';
      this.#activeItem.style.removeProperty('--box-height');
      delete this.#activeItem.dataset.safetyBox;
    }

    if (this.#activeSubmenu) {
      delete this.#activeSubmenu.dataset.active;
    }

    const listItem = item.closest('.menu-list__list-item');

    this.#isOverflow = listItem?.getAttribute('slot') === 'overflow';
    this.#activeItem = item;
    this.#activeSubmenu = this.#findSubmenu(item);

    const panel = this.#activeSubmenu || (
      this.#isOverflow ? this.overflowMenu : null
    );

    if (!panel) {
      this.#closeImmediately();
      return;
    }

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.dataset.overflowExpanded = String(this.#isOverflow);

    panel.dataset.active = '';
    this.style.setProperty('--submenu-opacity', '1');

    panel.addEventListener('pointerenter', this.#cancelClose);
    panel.addEventListener('pointerleave', this.deactivate);

    this.#observePanel(panel);
    this.#measure();
    this.#scheduleMeasure();
  };

  deactivate = (event) => {
    if (!this.#activeItem) return;

    const nextTarget = event.relatedTarget;

    if (
      nextTarget instanceof Node &&
      this.#containsActiveTarget(nextTarget)
    ) {
      this.#cancelClose();
      return;
    }

    this.#cancelClose();

    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;

      if (!this.#activeItem) return;

      const listItem = this.#activeItem.closest(
        '.menu-list__list-item'
      );

      const panel = this.#activeSubmenu || (
        this.#isOverflow ? this.overflowMenu : null
      );

      const isHovered =
        listItem?.matches(':hover') ||
        panel?.matches(':hover') ||
        (
          this.#isOverflow &&
          (
            this.overflowMenu?.matches(':hover') ||
            this.overflowList?.matches(':hover')
          )
        );

      const focusedElement = document.activeElement;

      const hasKeyboardFocus =
        focusedElement instanceof Element &&
        focusedElement.matches(':focus-visible') &&
        this.#containsActiveTarget(focusedElement);

      if (isHovered || hasKeyboardFocus) return;

      this.#closeImmediately();
    }, 220);
  };

  #containsActiveTarget(target) {
    const listItem = this.#activeItem?.closest(
      '.menu-list__list-item'
    );

    if (
      listItem?.contains(target) ||
      this.#activeSubmenu?.contains(target)
    ) {
      return true;
    }

    return Boolean(
      this.#isOverflow &&
      (
        this.overflowMenu?.contains(target) ||
        this.overflowList?.contains(target)
      )
    );
  }

  #observePanel(panel) {
    this.#resizeObserver = new ResizeObserver(() => {
      this.#scheduleMeasure();
    });

    this.#mutationObserver = new MutationObserver(() => {
      this.#observeSizeTargets(panel);
      this.#scheduleMeasure();
    });

    this.#observeSizeTargets(panel);

    this.#mutationObserver.observe(panel, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (this.#isOverflow && this.overflowMenu) {
      this.#resizeObserver.observe(this.overflowMenu);
    }
  }

  #observeSizeTargets(panel) {
    if (!this.#resizeObserver) return;

    this.#resizeObserver.disconnect();
    this.#resizeObserver.observe(panel);

    panel.querySelectorAll(
      '.menu-list__submenu-inner, .mega-menu__container'
    ).forEach((element) => {
      this.#resizeObserver.observe(element);
    });

    if (this.#isOverflow && this.overflowMenu) {
      this.#resizeObserver.observe(this.overflowMenu);
    }
  }

  #scheduleMeasure = () => {
    if (
      !this.isConnected ||
      !this.#activeItem ||
      this.#measureFrame !== null
    ) {
      return;
    }

    this.#measureFrame = requestAnimationFrame(() => {
      this.#measureFrame = null;
      this.#measure();
    });
  };

  #measure() {
    const header = this.headerComponent;

    if (!header || !this.#activeItem || !this.isConnected) {
      return;
    }

    const panel = this.#isOverflow
      ? this.overflowMenu
      : this.#activeSubmenu;

    if (!panel) return;

    const height = Math.ceil(panel.offsetHeight);

    if (height <= 0) return;

    const overlapsBottomRow = header.hasAttribute(
      'data-submenu-overlap-bottom-row'
    );

    const topRow = header.querySelector('.header__row--top');

    const headerHeight = overlapsBottomRow && topRow
      ? topRow.offsetHeight
      : header.offsetHeight;

    this.#setProperty(header, '--submenu-height', `${height}px`);
    this.#setProperty(
      header,
      '--full-open-header-height',
      `${height + headerHeight}px`
    );
  }

  #setProperty(element, name, value) {
    if (element.style.getPropertyValue(name) !== value) {
      element.style.setProperty(name, value);
    }
  }

  #stopObserving() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;

    if (this.#measureFrame !== null) {
      cancelAnimationFrame(this.#measureFrame);
      this.#measureFrame = null;
    }

    const panel = this.#activeSubmenu || (
      this.#isOverflow ? this.overflowMenu : null
    );

    panel?.removeEventListener('pointerenter', this.#cancelClose);
    panel?.removeEventListener('pointerleave', this.deactivate);
  }

  #closeImmediately() {
    this.#cancelClose();
    this.#stopObserving();

    if (this.#activeItem) {
      this.#activeItem.ariaExpanded = 'false';
      this.#activeItem.style.removeProperty('--box-height');
      delete this.#activeItem.dataset.safetyBox;
    }

    if (this.#activeSubmenu) {
      delete this.#activeSubmenu.dataset.active;
    }

    if (this.#isOverflow && this.overflowMenu) {
      delete this.overflowMenu.dataset.active;
    }

    this.#activeItem = null;
    this.#activeSubmenu = null;
    this.#isOverflow = false;

    this.ariaExpanded = 'false';
    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');

    const header = this.headerComponent;

    if (header) {
      this.#setProperty(header, '--submenu-height', '0px');
      this.#setProperty(header, '--full-open-header-height', '0px');
    }
  }

  #findMenuItem(target) {
    const moreItem = target.closest('[slot="more"]');

    if (moreItem && this.contains(moreItem)) {
      return this.querySelector(
        '.menu-list__list-item[slot="overflow"] [ref="menuitem"]'
      );
    }

    const listItem = target.closest('.menu-list__list-item');

    if (!listItem || !this.contains(listItem)) return null;

    return listItem.querySelector('[ref="menuitem"]');
  }

  #findSubmenu(item) {
    const submenu = item.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

    return submenu instanceof HTMLElement ? submenu : null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}